import { useState, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import { UserContext } from '../../../Contexts/UserContext';
import { screenerList, screenerListDelete, screenerAction } from '../../../Config/config';
import { DEFAULT_FILTERS, DEFAULT_SCREENER_NAME } from './useScreenerData';

/**
 * Manages the screener list: fetch, select, save, delete.
 */
const useScreenerList = ({
  fetchTableData,
  setFilters,
  setNotificationCmpIds,
  resetData,
}) => {
  const [screenerLists, setScreenerLists] = useState([]);
  const [screenerName, setScreenerName] = useState('');
  const [selectedScreenerId, setSelectedScreenerId] = useState(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const { getToken, getUser } = useContext(UserContext);

  // ─── Fetch list ────────────────────────────────────────────────────
  const refreshScreenerListOnly = useCallback(async () => {
    try {
      const response = await fetch(screenerList, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({ user_id: getUser()?.id }),
      });
      const data = await response.json();
      if (response.ok) {
        setScreenerLists(data?.screenerfilter || []);
      }
    } catch (err) {
      console.log('Error refreshing screener list:', err);
    }
  }, [getToken, getUser]);

  const fetchScreenerList = useCallback(
    async (selectScreenerId = null, cmpIds = null) => {
      try {
        const response = await fetch(screenerList, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body: JSON.stringify({ user_id: getUser()?.id }),
        });
        const data = await response.json();

        if (response.ok) {
          const screeners = data?.screenerfilter || [];
          setScreenerLists(screeners);

          if (selectScreenerId && screeners.length > 0) {
            const target = screeners.find((s) => s.id === selectScreenerId);
            const parsed =
                  typeof target.filters === 'string' ? JSON.parse(target.filters) : target.filters;
            if (target) {
              setScreenerName(target.name || '');
              setSelectedScreenerId(target.id);
              if (cmpIds) {
                setNotificationCmpIds(cmpIds);
                fetchTableData({ page: 1 }, target.id, cmpIds);
                setFilters(parsed);
                return;
              }
              if (target.filters) {
                setFilters(parsed);
                fetchTableData(parsed, target.id);
              }
              return;
            }
          }

          // Default: load first screener
          if (screeners.length > 0 && !selectScreenerId) {
            const first = screeners[0];
            setScreenerName(first.name || '');
            setSelectedScreenerId(first.id);
            if (first.filters) {
              const parsed =
                typeof first.filters === 'string' ? JSON.parse(first.filters) : first.filters;
              setFilters(parsed);
              fetchTableData(parsed, first.id);
            }
          }

          // No saved screeners — apply default filters
          if (screeners.length === 0 && !selectScreenerId) {
            setScreenerName(DEFAULT_SCREENER_NAME);
            setFilters(DEFAULT_FILTERS);
            fetchTableData(DEFAULT_FILTERS);
          }
        }
      } catch (err) {
        console.log('Error fetching screener list:', err);
      }
    },
    [getToken, getUser, fetchTableData, setFilters, setNotificationCmpIds],
  );

  // ─── Select ────────────────────────────────────────────────────────
  const handleScreenerSelect = useCallback(
    (screenerId) => {
      setNotificationCmpIds(null);
      const selected = screenerLists.find((s) => s.id === screenerId);
      if (!selected) return;

      setSelectedScreenerId(screenerId);
      setScreenerName(selected.name || '');

      const filterData = selected.filters || selected.payload;
      if (filterData) {
        const parsed = typeof filterData === 'string' ? JSON.parse(filterData) : filterData;
        setFilters(parsed);
        fetchTableData(parsed, screenerId);
      } else {
        fetchTableData({ _placeholder: true }, screenerId);
      }
    },
    [screenerLists, fetchTableData, setFilters, setNotificationCmpIds],
  );

  // ─── Save ──────────────────────────────────────────────────────────
  const handleSaveFilters = useCallback(
    async (filters, tableData, pendingAction, executePendingAction) => {
      if (!screenerName.trim()) return;
      setSaveLoading(true);

      try {
        const { page, ...filtersWithoutPage } = filters;
        const payload = {
          name: screenerName,
          request_json: JSON.stringify(filtersWithoutPage),
          user_id: getUser()?.id,
          all_cos_ids: tableData.all_cos_ids,
        };

        if (selectedScreenerId) {
          payload.screener_id = selectedScreenerId;
        }

        const response = await fetch(screenerAction, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (response.ok) {
          setIsSaveModalOpen(false);
          toast.success(data.message || 'Screener saved successfully!', { position: 'top-center' });
          setSelectedScreenerId(data.screener_id);

          // Execute pending company action if any
          if (pendingAction && data?.screener_id) {
            await executePendingAction(pendingAction.companyIds, pendingAction.action, data.screener_id);
          }

          await refreshScreenerListOnly();
        } else {
          toast.error(data?.message || 'Failed to save screener', { position: 'top-center' });
        }
      } catch (error) {
        console.error('Error saving screener:', error);
        toast.error('Error saving screener. Please try again.', { position: 'top-center' });
      } finally {
        setSaveLoading(false);
      }
    },
    [screenerName, selectedScreenerId, getToken, getUser, refreshScreenerListOnly],
  );

  // ─── Delete ────────────────────────────────────────────────────────
  const handleDeleteScreener = useCallback(
    async (screenerId) => {
      try {
        const response = await fetch(screenerListDelete, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body: JSON.stringify({ screener_id: screenerId }),
        });
        const data = await response.json();

        if (response.ok) {
          toast.success(data?.message || 'Screener deleted successfully!', { position: 'top-center' });
          if (selectedScreenerId === screenerId) {
            setSelectedScreenerId(null);
            setScreenerName('Untitled');
            resetData();
          }
          await refreshScreenerListOnly();
        } else {
          toast.error(data?.message || 'Failed to delete screener', { position: 'top-center' });
        }
      } catch (error) {
        console.error('Error deleting screener:', error);
        toast.error('Error deleting screener. Please try again.', { position: 'top-center' });
      }
    },
    [getToken, selectedScreenerId, resetData, refreshScreenerListOnly],
  );

  // ─── Helpers for filter-update confirmation flow ───────────────────
  const detachScreener = useCallback(
    (newFilters) => {
      setSelectedScreenerId(null);
      setScreenerName('Untitled');
      if (newFilters) setFilters(newFilters);
    },
    [setFilters],
  );

  return {
    screenerLists,
    screenerName,
    setScreenerName,
    selectedScreenerId,
    setSelectedScreenerId,
    isSaveModalOpen,
    setIsSaveModalOpen,
    saveLoading,

    fetchScreenerList,
    refreshScreenerListOnly,
    handleScreenerSelect,
    handleSaveFilters,
    handleDeleteScreener,
    detachScreener,
  };
};

export default useScreenerList;
